import {
  Component,
  computed, effect, inject, input, model, output, signal
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { DatePickerModule } from "primeng/datepicker";
import { BottomSheetComponent } from "../../../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { SelectFieldComponent } from "../../../../../../../shared/components/select-field/select-field.component";
import { SendToContactComponent } from "../send-to-contact/send-to-contact.component";
import { JobIssueVendor, UserVendorContact } from "../../../../../../../core/database/db";
import { UiToastService } from "../../../../../../../shared/components/toast/ui-toast.service";
import { LoggerService } from "../../../../../../../shared/services/logger.service";
import { isValidEmail } from "../../../../../../../shared/utils/email-helper";
import { JobIssueWithDetails } from "../../../../../../../domains/jobIssues/models/JobIssueWithDetails";
import { ASSIGN_TO_ME_OPTION_LABEL, SELF_ASSIGNED_VENDOR, VendorDTO, withAssignToMeOption } from "../../../../../../../domains/vendors/models/VendorDTO";
import { OpenItemsDataService } from "../../../services/open-items-data.service";

export interface AssignItemsResult {
  externalVendorId: number;
  dueDate?: Date;
  isSent: boolean;
  issueIdentifiers: string[];
  assignments: JobIssueVendor[];
}

@Component({
  selector: "app-assign-items-to-trade-bottom-sheet",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    BottomSheetComponent,
    SelectFieldComponent,
    SendToContactComponent
  ],
  templateUrl: "./assign-items-to-trade-bottom-sheet.component.html",
  styleUrls: ["./assign-items-to-trade-bottom-sheet.component.scss"]
})
export class AssignItemsToTradeBottomSheetComponent {
  constructor(
    private toastService: UiToastService,
    private logger: LoggerService
  ) {
    effect(() => {
      if (!this.isOpen()) {
        this.resetState();
      }
    });
  }

  openItemsDataService = inject(OpenItemsDataService);

  isOpen = model<boolean>(false);
  selectedIssues = input.required<JobIssueWithDetails[]>();

  assigned = output<AssignItemsResult>();

  selectedVendorId = signal<number | null>(null);
  checkedContactIdsForSelectedVendor = signal<number[]>([]);
  dueDate = signal<Date | null>(null);
  isSaving = signal<boolean>(false);
  isSending = signal<boolean>(false);

  vendors = computed(() => this.openItemsDataService.vendors() ?? (() => {
    throw new Error("Vendor data is not available in OpenItemsDataService.");
  })());

  /** Trade partners with "Assign to Me" pinned to the top. */
  vendorOptions = computed(() => withAssignToMeOption(this.vendors()));

  protected readonly selfAssignedVendorId = SELF_ASSIGNED_VENDOR.id;
  protected readonly assignToMeLabel = ASSIGN_TO_ME_OPTION_LABEL;

  userVendorContacts = computed(() => this.openItemsDataService.userVendorContacts() ?? []);

  financialCommunityId = computed(() => this.openItemsDataService.financialCommunityId() ?? (() => {
    throw new Error("Financial community ID is not available in OpenItemsDataService.");
  })());

  selectedVendor = computed(() => {
    const id = this.selectedVendorId();
    return id ? this.vendorOptions().find(v => v.id === id) ?? null : null;
  });

  /** Self-assigned items skip the send step, so there are no contacts or "Assign & Send". */
  isAssignToMeSelected = computed(() => this.selectedVendorId() === SELF_ASSIGNED_VENDOR.id);

  canAssign = computed(() =>
    this.selectedVendorId() !== null &&
    this.selectedVendor() !== null &&
    !this.isSaving() &&
    !this.isSending()
  );

  itemCount = computed(() => this.selectedIssues().length);

  contactsForSelectedVendor = computed(() => {
    const vendorId = this.selectedVendorId();
    return vendorId ? this.userVendorContacts().filter(c => c.externalVendorId === vendorId) : [];
  });

  checkedContactsForSend = computed(() => this.userVendorContacts().filter(c => c.id && this.checkedContactIdsForSelectedVendor().includes(c.id)));

  onVendorChange(vendorId: number | null): void {
    this.selectedVendorId.set(vendorId);
  }

  onSelectionChange(selected: UserVendorContact[]): void {
    const selectedIds = selected.filter(x => !!x.id).map(x => x.id!);
    this.checkedContactIdsForSelectedVendor.set(selectedIds);
  }

  async onAssignAndSave(): Promise<void> {
    if (this.canAssign() === false) {
      return;
    }
    this.isSaving.set(true);
    try {
      const selectedIssues = this.selectedIssues();
      const selectedVendor = this.selectedVendor()!;
      const dueDate = this.dueDate() ?? undefined;

      const assignments = await this.openItemsDataService.bulkAssignVendorToIssues(selectedIssues, selectedVendor, dueDate);
      this.toastService.success(`${this.itemCount()} item${this.itemCount() === 1 ? "" : "s"} assigned to ${this.assigneeDisplayName(selectedVendor)}`);
      this.assigned.emit({
        externalVendorId: selectedVendor.id,
        dueDate,
        isSent: true,
        issueIdentifiers: selectedIssues.map(i => i.identifier),
        assignments
      });
      this.close();
    } catch (error) {
      this.logger.error("Error assigning vendor to issues", error);
      this.toastService.error("Unable to assign vendor right now");
    } finally {
      this.isSaving.set(false);
    }
  }

  async onAssignAndSend(): Promise<void> {
    if (!this.canAssign() || this.isAssignToMeSelected()) {
      return;
    }

    const contactsToSendTo = this.checkedContactsForSend();
    if (contactsToSendTo.length === 0) {
      this.toastService.warn("Select at least one contact before sending to trade");
      return;
    }
    if (contactsToSendTo.some(c => !isValidEmail(c.email ?? ""))) {
      this.toastService.error("One or more email addresses are invalid");
      return;
    }

    if (navigator.onLine === false) {
      this.toastService.warn("Cannot send issues to vendor while offline.");
      return;
    }

    this.isSending.set(true);
    try {
      const selectedIssues = this.selectedIssues();
      const selectedVendor = this.selectedVendor()!;
      const dueDate = this.dueDate() ?? undefined;
      const checkedContactsForSend = this.checkedContactsForSend();

      const assignments = await this.openItemsDataService.bulkAssignVendorToIssues(selectedIssues, selectedVendor, dueDate);
      const success = await this.openItemsDataService.sendIssuesToVendor(selectedIssues, assignments, selectedVendor, checkedContactsForSend);

      if (success) {
        const count = selectedIssues.length;
        this.toastService.success(`${count} item${count === 1 ? "" : "s"} sent to ${(selectedVendor.doingBusinessAs || selectedVendor.name) ?? "vendor"}`);
        this.assigned.emit({
          externalVendorId: selectedVendor.id,
          dueDate,
          isSent: true,
          issueIdentifiers: selectedIssues.map(i => i.identifier),
          assignments
        });
        this.close();
      } else {
        this.toastService.error("Unable to send items to vendor");
      }
    } catch (error) {
      this.logger.error("Error sending issues to vendor", error);
      this.toastService.error("Unable to send items right now");
    } finally {
      this.isSending.set(false);
    }
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.isOpen.set(false);
  }

  private assigneeDisplayName(vendor: VendorDTO): string {
    if (vendor.id === SELF_ASSIGNED_VENDOR.id) {
      return "you";
    }

    return (vendor.doingBusinessAs || vendor.name) ?? "vendor";
  }

  private resetState(): void {
    this.selectedVendorId.set(null);
    this.dueDate.set(null);
    this.isSaving.set(false);
    this.isSending.set(false);
  }
}
